import DataGrid, {
    Column,
    Selection,
} from "devextreme-react/cjs/data-grid";
import notistack from "modules/core/notistack";
import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { getAllEcomSalesOrdersItems } from "../services/ecomSalesOrders.service";
import CreateShipmentModal from "./CreateShipmentModal";

interface ViewEcomSalesOrderItemsProps {
    isOpen: boolean;
    onClose: () => void;
    reloadDataSource: () => void;
    selectedId: number | undefined;
}

const ViewEcomSalesOrderItems: React.FC<
    ViewEcomSalesOrderItemsProps
> = ({ isOpen, onClose, selectedId, reloadDataSource }) => {
    const [data, setData] = useState<any[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [shipmentModalOpen, setShipmentModalOpen] =
        useState(false);

    const getSalesItems = async (salesOrderId: number) => {
        try {
            const response = await getAllEcomSalesOrdersItems({
                salesOrderId,
            });
            setData(response.data);
        } catch (error: any) {
            setData([]);
            notistack.error(error);
        }
    };

    useEffect(() => {
        if (isOpen && selectedId) {
            getSalesItems(selectedId);
        }
    }, [selectedId, isOpen]);

    const handleSelectionChanged = (e: any) => {
        const validRows = e.selectedRowsData.filter(
            (item: any) =>
                item?.ecomStatus === undefined ||
                item?.ecomStatus === null ||
                typeof item?.ecomStatus !== "string"
        );

        setSelectedRows(validRows);
    };

    const isRowDisabled = (data: any) => {
        return typeof data?.ecomStatus === "string";
    };

    const handleCellPrepared = useCallback((e: any) => {
        if (
            e.rowType === "data" &&
            e.column.command === "select" &&
            isRowDisabled(e.data)
        ) {
            e.cellElement.innerHTML = "";
        }
    }, []);

    const handleRowPrepared = useCallback((e: any) => {
        if (e.rowType === "data" && isRowDisabled(e.data)) {
            e.rowElement.classList.add("dx-row-disabled");
        }
    }, []);

    return (
        <>
            <Modal
                show={isOpen}
                onHide={onClose}
                size="xl"
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        E-commerce Sales Order Items
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="mb-3 text-end">
                        <Button
                            className="btn submit-btn"
                            disabled={selectedRows.length === 0}
                            onClick={() => setShipmentModalOpen(true)}
                        >
                            Create Shipment
                        </Button>
                    </div>

                    <DataGrid
                        dataSource={data}
                        remoteOperations={true}
                        showRowLines={true}
                        keyExpr="id"
                        showBorders={true}
                        columnAutoWidth={true}
                        onSelectionChanged={handleSelectionChanged}
                        onRowPrepared={handleRowPrepared}
                        onCellPrepared={handleCellPrepared}
                    >
                        <Selection mode="multiple" />

                        <Column
                            dataField="id"
                            dataType="number"
                            sortOrder="desc"
                            visible={false}
                        />

                        <Column dataField="invoiceNo" caption="Invoice No." />

                        <Column
                            dataField="soDate"
                            caption="Invoice Date"
                            dataType="date"
                            format="dd/MM/yyyy"
                        />

                        <Column dataField="category" caption="Category" />

                        <Column dataField="style" caption="Style No." />

                        <Column dataField="karat" caption="Karat" />

                        <Column
                            dataField="amount"
                            caption="Amount"
                            format="#,##0.00"
                        />

                        <Column
                            dataField="ecomStatus"
                            caption="Status"
                        />
                    </DataGrid>
                </Modal.Body>
            </Modal>

            <CreateShipmentModal
                selectedId={selectedId}
                isOpen={shipmentModalOpen}
                onClose={() => {
                    onClose()
                    reloadDataSource()
                    setShipmentModalOpen(false)
                }}
                selectedItems={selectedRows}
            />
        </>
    );
};

export default ViewEcomSalesOrderItems;